const hasNonEmptyValue = (value) => {
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "string") return value.trim() !== "";
  return value !== null && value !== undefined && value !== "";
};

/** Integer / numeric input questions, including Paragraph-Type (Integer-Type). */
export const isIntegerTypeQuestion = (question = {}) => {
  const answerTypeId = Number(question?.answer_type_id);
  if (answerTypeId === 3) return true;

  const typeLabel = String(
    question?.type ?? question?.answer_type ?? question?.question_type ?? ""
  ).toLowerCase();

  return typeLabel.includes("integer");
};

export const getQuestionIntegerAnswer = (question = {}) => {
  const integerValue =
    question?.integerAnswer ??
    question?.integer_Answer ??
    question?.integer_answer ??
    question?.option_int ??
    "";
  if (integerValue === null || integerValue === undefined) return "";
  return String(integerValue);
};

/** Payload fields for exam_single_save — always send option_int for integer types. */
export const getExamSaveAnswerFields = (question = {}) => {
  if (isIntegerTypeQuestion(question)) {
    return { option_int: getQuestionIntegerAnswer(question) };
  }

  const selected = question?.selectedOption;
  return {
    option_id:
      selected === null || selected === undefined ? "" : selected,
  };
};

export const hasQuestionAnswer = (question = {}) => {
  const optionValues = [
    question.selectedOption,
    question.selected_option,
    question.option_id,
    question.optionId,
    question.answer_id,
    question.answerId,
  ];

  const hasOptionAnswer = optionValues.some((value) => hasNonEmptyValue(value));

  const integerValues = [
    question.integerAnswer,
    question.integer_Answer,
    question.integer_answer,
    question.option_int,
    question.answer_int,
  ];

  const hasIntegerAnswer = integerValues.some((value) => {
    if (Array.isArray(value)) return value.length > 0;
    if (value === null || value === undefined) return false;
    return String(value).trim() !== "";
  });

  return hasOptionAnswer || hasIntegerAnswer;
};

export const hasCommittedExamAnswer = (question = {}) =>
  Number(question?.is_saved) === 1 && hasQuestionAnswer(question);

export const normalizeExamQuestionStatus = (question = {}) => {
  const rawStatus = String(question.status ?? question.questionStatus ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

  switch (rawStatus) {
    case "current":
      return "current";
    case "answered":
      return "answered";
    case "unanswered":
    case "not_answered":
    case "skip":
    case "skipped":
      return "unanswered";
    case "marked":
    case "mark_for_review":
    case "marked_for_review":
    case "review":
      return "marked";
    case "answered_marked":
    case "answered_and_marked":
    case "answered_marked_for_review":
      return "answered_marked";
    case "unvisited":
    case "not_visited":
    case "notvisited":
      return "unvisited";
    default:
      break;
  }

  const numericStatus = Number(question.question_status);
  if (Number.isFinite(numericStatus)) {
    switch (numericStatus) {
      case 0:
        return "unvisited";
      case 1:
        return "answered";
      case 2:
        return "unanswered";
      case 3:
        return "marked";
      case 4:
        return "answered_marked";
      default:
        break;
    }
  }

  if (question.marked) {
    return hasCommittedExamAnswer(question) ? "answered_marked" : "marked";
  }

  if (hasCommittedExamAnswer(question)) return "answered";
  if (question.status || Number(question.question_status) === 2) return "unanswered";
  return "unvisited";
};

export const getBackendQuestionStatus = (question = {}) => {
  const status = normalizeExamQuestionStatus(question);

  switch (status) {
    case "answered":
      return 1;
    case "unanswered":
      return 2;
    case "marked":
      return 3;
    case "answered_marked":
      return 4;
    case "current":
      if (question.marked) {
        return hasCommittedExamAnswer(question) ? 4 : 3;
      }
      return hasCommittedExamAnswer(question) ? 1 : 2;
    case "unvisited":
    default:
      return 0;
  }
};
